const GOOGLE_API_SCRIPT = "https://apis.google.com/js/api.js";
const DRIVE_FILES_API = "https://www.googleapis.com/drive/v3/files";

type PickerConfig = {
  accessToken: string;
  clientId: string;
  apiKey: string;
  appId: string;
};

export type PickedDriveItem = {
  id: string;
  name: string;
  mimeType: string;
  isFolder: boolean;
  parentId?: string;
};

export type InferredDriveFolder = {
  folderId: string;
  folderName: string;
};

/**
 * Resolve display names for parent folders of picked files.
 * Under drive.file the folder itself may not be readable; fall back to a
 * generic name so we can still group files in settings.
 */
export async function resolveParentFolders(
  accessToken: string,
  parentIds: string[],
  fallbackName: string,
): Promise<InferredDriveFolder[]> {
  const uniqueIds = Array.from(
    new Set(parentIds.map((id) => id.trim()).filter(Boolean)),
  );
  return Promise.all(
    uniqueIds.map(async (folderId) => {
      let folderName = fallbackName;
      try {
        const url = new URL(`${DRIVE_FILES_API}/${encodeURIComponent(folderId)}`);
        url.searchParams.set("fields", "name");
        url.searchParams.set("supportsAllDrives", "true");
        const response = await fetch(url.toString(), {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (response.ok) {
          const data = (await response.json()) as { name?: unknown };
          if (typeof data.name === "string" && data.name.trim()) {
            folderName = data.name.trim();
          }
        }
      } catch {
        // Keep fallback name when Drive metadata is unavailable.
      }
      return { folderId, folderName };
    }),
  );
}

declare global {
  interface Window {
    gapi?: {
      load: (api: string, callback: () => void) => void;
    };
    google?: {
      accounts?: {
        id?: {
          cancel: () => void;
        };
      };
      picker?: {
        Action: { PICKED: string; CANCEL: string };
        DocsViewMode: { LIST: string };
        Feature: { MULTISELECT_ENABLED: string; SUPPORT_DRIVES: string };
        Response: { ACTION: string; DOCUMENTS: string };
        Document: {
          ID: string;
          NAME: string;
          MIME_TYPE: string;
          PARENT_ID?: string;
        };
        ViewId: { DOCS: string; FOLDERS: string };
        DocsView: new (viewId?: string) => {
          setIncludeFolders: (include: boolean) => GooglePickerView;
          setSelectFolderEnabled: (enabled: boolean) => GooglePickerView;
          setMimeTypes: (mimeTypes: string) => GooglePickerView;
          setMode: (mode: string) => GooglePickerView;
        };
        PickerBuilder: new () => {
          addView: (view: unknown) => GooglePickerBuilder;
          enableFeature: (feature: string) => GooglePickerBuilder;
          setAppId: (appId: string) => GooglePickerBuilder;
          setDeveloperKey: (key: string) => GooglePickerBuilder;
          setOAuthToken: (token: string) => GooglePickerBuilder;
          setOrigin: (origin: string) => GooglePickerBuilder;
          setRelayUrl: (url: string) => GooglePickerBuilder;
          setCallback: (
            callback: (data: Record<string, unknown>) => void,
          ) => GooglePickerBuilder;
          setTitle: (title: string) => GooglePickerBuilder;
          build: () => { setVisible: (visible: boolean) => void };
        };
      };
    };
  }
}

type GooglePickerView = {
  setIncludeFolders: (include: boolean) => GooglePickerView;
  setSelectFolderEnabled: (enabled: boolean) => GooglePickerView;
  setMimeTypes: (mimeTypes: string) => GooglePickerView;
  setMode: (mode: string) => GooglePickerView;
};

type GooglePickerBuilder = {
  addView: (view: unknown) => GooglePickerBuilder;
  enableFeature: (feature: string) => GooglePickerBuilder;
  setAppId: (appId: string) => GooglePickerBuilder;
  setDeveloperKey: (key: string) => GooglePickerBuilder;
  setOAuthToken: (token: string) => GooglePickerBuilder;
  setOrigin: (origin: string) => GooglePickerBuilder;
  setRelayUrl: (url: string) => GooglePickerBuilder;
  setCallback: (
    callback: (data: Record<string, unknown>) => void,
  ) => GooglePickerBuilder;
  setTitle: (title: string) => GooglePickerBuilder;
  build: () => { setVisible: (visible: boolean) => void };
};

let gapiLoadPromise: Promise<void> | null = null;

function loadGapi(): Promise<void> {
  if (window.gapi?.load) {
    return Promise.resolve();
  }
  if (gapiLoadPromise) {
    return gapiLoadPromise;
  }

  gapiLoadPromise = new Promise((resolve, reject) => {
    const fail = (error: Error) => {
      gapiLoadPromise = null;
      document
        .querySelectorAll(`script[src="${GOOGLE_API_SCRIPT}"]`)
        .forEach((script) => script.remove());
      reject(error);
    };

    const existing = document.querySelector(
      `script[src="${GOOGLE_API_SCRIPT}"]`,
    ) as HTMLScriptElement | null;
    // A leftover script without gapi means a prior load failed — replace it.
    if (existing) {
      existing.remove();
    }

    const script = document.createElement("script");
    script.src = GOOGLE_API_SCRIPT;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => fail(new Error("Failed to load Google API script"));
    document.body.appendChild(script);
  });

  return gapiLoadPromise;
}

function loadPickerApi(): Promise<void> {
  return loadGapi().then(
    () =>
      new Promise((resolve, reject) => {
        if (!window.gapi?.load) {
          reject(new Error("Google API is unavailable"));
          return;
        }
        window.gapi.load("picker", () => resolve());
      }),
  );
}

const FOLDER_MIME = "application/vnd.google-apps.folder";
const PICKER_ICON_SELECTOR = 'link[rel="icon"], link[rel="shortcut icon"]';

function pickerOrigin(): string {
  return `${window.location.protocol}//${window.location.host}`;
}

/**
 * Google Picker defaults `parent` to the page favicon (`/favicon.ico`).
 * Restricted API keys treat that as an invalid referrer, so point icon
 * links at the origin while the picker is open.
 */
function rewriteFaviconToOrigin(): () => void {
  const origin = pickerOrigin();
  const icons = Array.from(
    document.querySelectorAll<HTMLLinkElement>(PICKER_ICON_SELECTOR),
  );
  const previousHrefs = icons.map((icon) => icon.getAttribute("href"));
  icons.forEach((icon) => {
    icon.setAttribute("href", `${origin}/`);
  });
  return () => {
    icons.forEach((icon, index) => {
      const href = previousHrefs[index];
      if (href == null) {
        icon.removeAttribute("href");
      } else {
        icon.setAttribute("href", href);
      }
    });
  };
}

export async function openGoogleDrivePicker(
  config: PickerConfig,
  options?: { title?: string },
): Promise<PickedDriveItem[]> {
  await loadPickerApi();
  const pickerApi = window.google?.picker;
  if (!pickerApi) {
    throw new Error("Google Picker is unavailable");
  }

  return new Promise((resolve, reject) => {
    const restoreFavicon = rewriteFaviconToOrigin();
    const finish = (items: PickedDriveItem[]) => {
      restoreFavicon();
      resolve(items);
    };
    try {
      const origin = pickerOrigin();
      const docsView = new pickerApi.DocsView(pickerApi.ViewId.DOCS)
        .setIncludeFolders(true)
        .setSelectFolderEnabled(true)
        .setMode(pickerApi.DocsViewMode.LIST);

      const folderView = new pickerApi.DocsView(pickerApi.ViewId.FOLDERS)
        .setIncludeFolders(true)
        .setSelectFolderEnabled(true)
        .setMimeTypes(FOLDER_MIME);

      const picker = new pickerApi.PickerBuilder()
        .addView(docsView)
        .addView(folderView)
        .enableFeature(pickerApi.Feature.MULTISELECT_ENABLED)
        .enableFeature(pickerApi.Feature.SUPPORT_DRIVES)
        .setAppId(config.appId)
        .setDeveloperKey(config.apiKey)
        .setOAuthToken(config.accessToken)
        .setOrigin(origin)
        .setRelayUrl(origin)
        .setTitle(options?.title || "Select Drive folders and their files")
        .setCallback((data: Record<string, unknown>) => {
          const action = data[pickerApi.Response.ACTION];
          if (action === pickerApi.Action.CANCEL) {
            finish([]);
            return;
          }
          if (action !== pickerApi.Action.PICKED) {
            return;
          }
          const docs = (data[pickerApi.Response.DOCUMENTS] as
            | Array<Record<string, unknown>>
            | undefined) || [];
          finish(
            docs.map((doc) => {
              const mimeType =
                typeof doc[pickerApi.Document.MIME_TYPE] === "string"
                  ? (doc[pickerApi.Document.MIME_TYPE] as string)
                  : "";
              const parentKey = pickerApi.Document.PARENT_ID || "parentId";
              const rawParent = doc[parentKey] ?? doc.parentId;
              let parentId: string | undefined;
              if (typeof rawParent === "string") {
                parentId = rawParent;
              } else if (
                Array.isArray(rawParent) &&
                typeof rawParent[0] === "string"
              ) {
                parentId = rawParent[0];
              }
              const id =
                typeof doc[pickerApi.Document.ID] === "string"
                  ? (doc[pickerApi.Document.ID] as string)
                  : String(doc[pickerApi.Document.ID] ?? "");
              const name =
                typeof doc[pickerApi.Document.NAME] === "string"
                  ? (doc[pickerApi.Document.NAME] as string)
                  : String(doc[pickerApi.Document.NAME] ?? "");
              return {
                id,
                name,
                mimeType,
                isFolder: mimeType === FOLDER_MIME,
                parentId: parentId || undefined,
              };
            }),
          );
        })
        .build();

      picker.setVisible(true);
    } catch (error) {
      restoreFavicon();
      reject(error);
    }
  });
}
